/**
 * Message pressure — slice 59, Sense's first signal.
 *
 * ## WHAT IS MEASURED
 *
 * **A touch is a message a provider accepted** (`messages.sent_at` set), on
 * email or WhatsApp, to one PERSON (slice 58), in the last
 * {@link PRESSURE_WINDOW_DAYS} days. Not a recipient row (a skipped row reached
 * nobody), not a delivery event (SES and Meta re-notify, and a bounced message
 * still pressed on somebody's inbox the moment it was accepted), not a click.
 * Meta ads are not counted: an ad is shown to an audience Meta chooses, not
 * handed to a person this workspace can name.
 *
 * Unlike almost every other figure in this product, a touch count is never "not
 * measured": Wend knows every message it handed to a provider, so a person with
 * no messages in the window has a real 0 — the zero-versus-absent rule is
 * satisfied by the zero being true.
 *
 * ## THE BANDS, AND WHAT THEY DO NOT CLAIM
 *
 *   Healthy   0–2 touches in 30 days
 *   Warning   3–4
 *   Critical  5 or more
 *
 * They come from the owner's Sense concept and are written once, here. They
 * describe how often somebody has been messaged and nothing else: a person at
 * Critical may be answering every one. Whether people are still responding is
 * a separate figure, shown only where it is measured, and never folded into the
 * band — a band that mixed a count Wend always has with a response rate it often
 * does not would turn "not measured" into a colour.
 *
 * ## WHY BY PERSON AND ACROSS CHANNELS
 *
 * Pressure is felt by a human, whichever channel it arrived on: three emails and
 * two WhatsApp messages in a month is five. That is why this signal waited for
 * slice 58 — counted by address, the same customer's email and phone would have
 * been two half-pressured people.
 */
import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common.js';
import { channelSchema } from '../enums.js';

/** The window every pressure figure is counted over, in days. */
export const PRESSURE_WINDOW_DAYS = 30;

/** Touches at which a person leaves Healthy. */
export const PRESSURE_WARNING_AT = 3;

/** Touches at which a person is at Critical. */
export const PRESSURE_CRITICAL_AT = 5;

export const PRESSURE_BAND_VALUES = ['healthy', 'warning', 'critical'] as const;
export const pressureBandSchema = z.enum(PRESSURE_BAND_VALUES);
export type PressureBand = z.infer<typeof pressureBandSchema>;

/** The band for a touch count. The only place the thresholds are compared. */
export function pressureBand(touches: number): PressureBand {
  if (touches >= PRESSURE_CRITICAL_AT) return 'critical';
  if (touches >= PRESSURE_WARNING_AT) return 'warning';
  return 'healthy';
}

/** How each band is named, and what range it covers, as a person reads it. */
export const PRESSURE_BAND_COPY: Readonly<Record<PressureBand, { readonly label: string; readonly range: string }>> = {
  healthy: { label: 'Healthy', range: `${String(PRESSURE_WARNING_AT - 1)} or fewer` },
  warning: { label: 'Warning', range: `${String(PRESSURE_WARNING_AT)}–${String(PRESSURE_CRITICAL_AT - 1)}` },
  critical: { label: 'Critical', range: `${String(PRESSURE_CRITICAL_AT)} or more` },
};

/** People in each band. Counts of people, never of rows or messages. */
export const pressureBandCountsSchema = z
  .object({
    healthy: z.number().int().min(0),
    warning: z.number().int().min(0),
    critical: z.number().int().min(0),
  })
  .strict();
export type PressureBandCounts = z.infer<typeof pressureBandCountsSchema>;

/** One segment, as the workspace page lists it — only segments with someone at Warning or worse. */
export const segmentPressureRowSchema = z
  .object({
    segmentId: uuidSchema,
    name: z.string(),
    /** People in the segment. */
    people: z.number().int().min(0),
    warning: z.number().int().min(0),
    critical: z.number().int().min(0),
  })
  .strict();
export type SegmentPressureRow = z.infer<typeof segmentPressureRowSchema>;

/** One of the most-messaged people. */
export const pressuredPersonSchema = z
  .object({
    personId: uuidSchema,
    /** From their most recent upload that has one; `null` when no upload named them. */
    name: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    touches: z.number().int().min(1),
    lastTouchedAt: isoDateTimeSchema,
    band: pressureBandSchema,
  })
  .strict();
export type PressuredPerson = z.infer<typeof pressuredPersonSchema>;

/** How many of the most-messaged people the page lists. */
export const PRESSURE_PEOPLE_LIMIT = 10;
/** How many segments the page lists. */
export const PRESSURE_SEGMENT_LIMIT = 5;

/** `GET /sense/pressure` — the whole workspace. */
export const workspacePressureSchema = z
  .object({
    windowDays: z.literal(PRESSURE_WINDOW_DAYS),
    generatedAt: isoDateTimeSchema,
    /** Everybody in the workspace: people with at least one contact row. */
    people: z.number().int().min(0),
    bands: pressureBandCountsSchema,
    /** Of the Healthy, how many had no message at all in the window. A real zero each. */
    untouched: z.number().int().min(0),
    /** Messages accepted in the window, all channels. Zero is a real zero. */
    touches: z.number().int().min(0),
    segments: z.array(segmentPressureRowSchema).max(PRESSURE_SEGMENT_LIMIT),
    /** Only people at Warning or worse, most-messaged first. */
    mostTouched: z.array(pressuredPersonSchema).max(PRESSURE_PEOPLE_LIMIT),
  })
  .strict()
  .superRefine((value, ctx) => {
    const banded = value.bands.healthy + value.bands.warning + value.bands.critical;
    if (banded !== value.people) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['bands'],
        message: `the bands must partition the people: ${String(banded)} banded, ${String(value.people)} people`,
      });
    }
    if (value.untouched > value.bands.healthy) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['untouched'], message: 'untouched people are a subset of Healthy' });
    }
  });
export type WorkspacePressure = z.infer<typeof workspacePressureSchema>;

/**
 * `GET /sense/pressure/segments/:segmentId?channel=` — what a send to this
 * segment on this channel would do, read at the schedule step.
 *
 * Over the people a campaign on that channel would try — each person's
 * representative row carrying an address (`representativeRowsSql`, slice 58) —
 * because a person the channel cannot reach is pressured by nothing this send
 * does.
 */
export const segmentPressureSchema = z
  .object({
    windowDays: z.literal(PRESSURE_WINDOW_DAYS),
    channel: channelSchema,
    /** People this channel would try. */
    people: z.number().int().min(0),
    bands: pressureBandCountsSchema,
    /** At one below Critical now, so this send would put them there. */
    reachingCritical: z.number().int().min(0),
  })
  .strict();
export type SegmentPressure = z.infer<typeof segmentPressureSchema>;

export const segmentPressureParamsSchema = z.object({ segmentId: uuidSchema }).strict();
export const segmentPressureQuerySchema = z.object({ channel: channelSchema }).strict();
export type SegmentPressureQuery = z.infer<typeof segmentPressureQuerySchema>;
